/**
 * Binary STL export of the 3D model the dollhouse view renders.
 *
 * Consumes the export scene buildScene3D records alongside the render mesh
 * (SceneSolids, as prisms, plus SceneWalls, each leaf with its cutouts as
 * real holes), lowered to planar boundary faces by prismFaces and wallBodies
 * and triangulated here. Each wall leaf triangulates to a single watertight
 * shell: material above, below, and beside every door and window is one
 * connected object.
 *
 * Output is millimeters, z-up (plan units are feet, world y-up), so the room
 * imports into slicers/CAD sitting flat at real-world scale.
 */

import { FloorPlanRoom } from '@kaiizen/kai-floor-plan';
import { cross, dot3d, length, normalize, sub, triangulatePolygon, Vec3 } from '../math3d';
import { buildScene3D } from './buildScene3d';
import { type ExportScene, type PlanarFace, type SpacePoint, toCenteredMillimeters } from './planarFace';
import { prismFaces, scenePrisms } from './prismFaces';
import { wallBodies } from './wallBodies';

// The view3d index publishes the serializers' input type from here.
export type { ExportScene };

interface StlTriangle {
  normal: Vec3;
  corners: [Vec3, Vec3, Vec3];
}

/** Appends a→b→c wound to agree with `outward`, if non-degenerate. */
const pushTriangle = (triangles: StlTriangle[], a: Vec3, b: Vec3, c: Vec3, outward: Vec3): void => {
  const facet = cross(sub(b, a), sub(c, a));
  if (length(facet) <= 1e-9) return;
  const flip = dot3d(facet, outward) < 0;
  triangles.push({
    normal: normalize(flip ? [-facet[0], -facet[1], -facet[2]] : facet),
    corners: flip ? [a, c, b] : [a, b, c],
  });
};

/** Every boundary face triangulated into STL space; degenerates dropped. */
const faceTriangles = (faces: PlanarFace[], toStl: (p: SpacePoint) => Vec3): StlTriangle[] => {
  const triangles: StlTriangle[] = [];
  for (const { rings, map, outward } of faces) {
    const outwardStl: Vec3 = [outward.x, outward.y, outward.h];
    for (const triangle of triangulatePolygon(rings)) {
      const [a, b, c] = triangle.map(p => toStl(map(p)));
      pushTriangle(triangles, a, b, c, outwardStl);
    }
  }
  return triangles;
};

/** 50 bytes: facet normal + 3 corners as little-endian float32, then a zero attribute word. */
const writeTriangle = (view: DataView, offset: number, { normal, corners }: StlTriangle): number => {
  for (const [x, y, z] of [normal, ...corners]) {
    view.setFloat32(offset, x, true);
    view.setFloat32(offset + 4, y, true);
    view.setFloat32(offset + 8, z, true);
    offset += 12;
  }
  view.setUint16(offset, 0, true);
  return offset + 2;
};

/**
 * Serializes a scene's export records to binary STL: 80-byte header, uint32
 * triangle count, 50 bytes per triangle. Millimeters, z-up (see the module doc).
 * One room (`roomToStl`) or a whole assembled level share this path.
 */
export const sceneToStl = ({ solids, walls, center }: ExportScene): ArrayBuffer => {
  const faces = [...scenePrisms(solids).flatMap(prismFaces), ...walls.flatMap(wall => wallBodies(wall).flat())];
  const triangles = faceTriangles(faces, toCenteredMillimeters(center));
  const buffer = new ArrayBuffer(84 + triangles.length * 50);
  new Uint8Array(buffer).set(new TextEncoder().encode('Kai floor plan (binary STL, mm, z-up)'));
  const view = new DataView(buffer);
  view.setUint32(80, triangles.length, true);
  let offset = 84;
  for (const triangle of triangles) offset = writeTriangle(view, offset, triangle);
  return buffer;
};

/** The room's 3D model as STL, centered on its primary rectangle (the scene's own center). */
export const roomToStl = (room: FloorPlanRoom): ArrayBuffer => sceneToStl(buildScene3D(room));
